import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { indexStore } from "../../../../store/indexStore";
import { Button } from "../../../ui/button";
import { DialogClose } from "src/Components/ui/dialog";
import FormFieldItem from "../../../../common/form/FormFieldItem";
import { Form, FormField } from "src/Components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import BranchTabs from "../BranchTabs";
import { Input } from "src/Components/ui/input";
import ImageSelected from "src/Components/Uploaders/ImageSelected";
import { IMG_URl } from "src/common/img_url";
import Visibility from "src/Components/utils/Visibility";
import { ComboboxForm } from "src/Components/selectbox/ComboBoxForm";
import { userTypes } from "src/enums/usersTpes";
import { Textarea } from "src/Components/ui/textarea";
import { Label } from "src/Components/ui/label";
import { Switch } from "src/Components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/Components/ui/card";
import { Building2, MapPin, Phone, Globe, ImageIcon, CheckCircle2, XCircle, Info, Navigation, AlertTriangle } from "lucide-react";

function BranchForm(props) {
  const { branchLoading, nameLang, restaurants } = indexStore();
  const { t } = useTranslation();
  const dialogCloseRef = useRef(null);
  // make validation and form fields values conditionally
  const formSchema = z.object({
    name_ar: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    name_en: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    address_ar: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    address_en: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    address_link: z
      .string({
        required_error: `${t("gloabl.addressLink")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.addressLink")} ${t(
          "errorMessage.fieldCharsLength"
        )}.`,
      }),
    active: z.any().optional(),
    mobile: z.string({
      required_error: `${t("gloabl.mobile")} ${t(
        "errorMessage.fieldRequired"
      )}.`,
    }),
    phone: z.any().optional(),
    image: z
      .union([
        z.instanceof(File).refine((file) => file.size < 4 * 1024 * 1024, {
          message: "File size must be less than 4MB",
          required_error: `${t("gloabl.image")} ${t(
            "errorMessage.fieldRequired"
          )}.`,
        }),
        z.string({
          required_error: `${t("gloabl.image")} ${t(
            "errorMessage.fieldRequired"
          )}.`,
        }),
      ])
      .optional(),
    latitude: z.any().optional(),
    longitude: z.any().optional(),
  });
  const [deleteImage, setDeleteImage] = useState(0);

  const [image, setImage] = useState("");

  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      ...props.datasource,
      image: image,
    },
  });

  const onErorr = (errors) => {
    console.log(errors);

    toast.error(t("errorMessage.fillAll"));
  };

  const onSubmit = (values) => {
    console.log(values.active);
    const submitValues = {
      ...values,
      delete_image: deleteImage,
      active: values.active ? 1 : 0,
    };
    console.log(submitValues);

    props.onSubmit(submitValues);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };

  useEffect(() => {
    if (props.editMode) {
      setImage(props.datasource.image ? IMG_URl + props.datasource.image : "");
    }
  }, [props.editMode]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-blue-200 dark:border-blue-900">
            <div className="p-2 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              <Info className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.basic_info") || "Basic Information"}</CardTitle>
              <CardDescription>{t("gloabl.branch_basic_info_desc") || "Enter branch name and address in both languages"}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <BranchTabs form={form} className="bg-transparent" />
          </CardContent>
        </Card>

        {/* Location & Contact */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-purple-200 dark:border-purple-900">
            <div className="p-2 rounded-lg bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.location_contact") || "Location & Contact"}</CardTitle>
              <CardDescription>{t("gloabl.location_contact_desc") || "Set branch location and contact information"}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name={"address_link"}
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      <span>{t("gloabl.addressLink")}</span>
                      <span className="text-destructive">*</span>
                    </div>
                  }
                  description={t("gloabl.address_link_desc") || "Google Maps link or location URL"}
                >
                  <Textarea {...field} placeholder="https://maps.google.com/..." />
                </FormFieldItem>
              )}
            />
            {/* Coordinates Warning */}
            {/* <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-200">
                {t("gloabl.coordinates_warning")}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      <div className="flex items-center gap-2">
                        <Navigation className="w-4 h-4" />
                        <span>{t("gloabl.latitude")}</span>
                      </div>
                    }
                  >
                    <Input type="text" {...field} placeholder="25.1972" dir="ltr" />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      <div className="flex items-center gap-2">
                        <Navigation className="w-4 h-4" />
                        <span>{t("gloabl.longitude")}</span>
                      </div>
                    }
                  >
                    <Input type="text" {...field} placeholder="55.2794" dir="ltr" />
                  </FormFieldItem>
                )}
              />
            </div> */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormFieldItem
                    required
                    form={form}
                    label={
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span>{t("gloabl.mobile")}</span>
                        <span className="text-destructive">*</span>
                      </div>
                    }
                  >
                    <Input type="tel" dir="ltr" {...field} placeholder="0501234567" />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span>{t("gloabl.phone")}</span>
                      </div>
                    }
                  >
                    <Input type="tel" dir="ltr" {...field} placeholder="04-1234567" />
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Image & Status */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-900">
            <div className="p-2 rounded-lg bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              {form.watch("active") ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.image_status") || "Image & Status"}</CardTitle>
              <CardDescription>{t("gloabl.image_status_desc") || "Upload branch image and set status"}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormFieldItem
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      <span>{t("gloabl.anImage")}</span>
                    </div>
                  }
                  description={t("messages.imageNotSelected")}
                >
                  <ImageSelected
                    form={form}
                    control={form.control}
                    name="image"
                    {...field}
                    editMode={props.editMode}
                    image={image}
                    setImage={setImage}
                    setDeleteImage={setDeleteImage}
                  />
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormFieldItem form={form}>
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      {field.value ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <Label className="text-sm font-medium cursor-pointer">
                          {t("gloabl.active")}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("gloabl.branch_status_desc") || "Enable or disable branch"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      dir="ltr"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit">{t("gloabl.save")}</Button>
        </div>
      </form>
    </Form>
  );
}

export default BranchForm;
